import { Button } from "@/components/ui/button";
import { SelectSearch } from "@/components/select-search";
import { PromotionForm } from "@/features/dash/registrations/forms/promotion-form";
import { api } from "@/lib/api";
import { ChevronsUpDown, Tag, Trash2 } from "lucide-react";
import type { PromotionBase } from "nautika-types";
import { useMemo, useState } from "react";

interface PromotionSelectorProps {
  selectedPromotionId: string | null;
  onPromotionChange: (promotionId: string | null) => void;
}

export function PromotionSelector({ selectedPromotionId, onPromotionChange }: PromotionSelectorProps) {
  const [showPromotionForm, setShowPromotionForm] = useState(false);

  // Récupération des promotions via l'API
  const { data: promotionsResponse, refetch, isLoading: loadingPromotions } = api.useQuery("get", "/promotions/");

  const promotions = useMemo(() => promotionsResponse || [], [promotionsResponse]);
  const selectedPromotion = promotions.find((p) => p.id === selectedPromotionId);

  const handlePromotionCreated = (newPromotion: PromotionBase) => {
    refetch(); // Rafraîchir la liste des promotions
    onPromotionChange(newPromotion.id); // Sélectionner automatiquement la nouvelle promotion
  };

  const formatPromotionValue = (promotion: (typeof promotions)[0]) => {
    if (promotion.type === "percentage") {
      return `${promotion.value}%`;
    } else {
      return `${(promotion.value / 100).toFixed(2)}€`;
    }
  };

  // Gérer la sélection de promotion
  const handlePromotionSelect = (promotion: (typeof promotions)[0]) => {
    // Toggle : si la promotion est déjà sélectionnée, la désélectionner, sinon la sélectionner
    onPromotionChange(selectedPromotionId === promotion.id ? null : promotion.id);
  };

  // Gérer la création d'une nouvelle promotion
  const handleCreateNew = () => {
    setShowPromotionForm(true);
  };

  // Fonction de filtrage personnalisée
  const filterPromotions = (items: typeof promotions, searchTerm: string) => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return items;
    return items.filter((p) => `${p.name} ${p.type} ${p.value}`.toLowerCase().includes(term));
  };

  // Rendu personnalisé pour chaque promotion
  const renderPromotionItem = (promotion: (typeof promotions)[0], isSelected?: boolean) => (
    <div className={`flex items-center gap-2 ${isSelected ? "font-bold" : ""}`}>
      <span>{promotion.name}</span>
      <span className="text-muted-foreground">({formatPromotionValue(promotion)})</span>
    </div>
  );

  // Rendu personnalisé du trigger
  const renderTrigger = () => (
    <Button variant="outline" role="combobox" className="w-[280px] justify-between">
      {selectedPromotion ? (
        <div className="flex items-center gap-2">
          <Tag className="h-4 w-4" />
          <span>{selectedPromotion.name}</span>
          <span className="text-muted-foreground">({formatPromotionValue(selectedPromotion)})</span>
        </div>
      ) : (
        <>
          <Tag className="mr-2 h-4 w-4" />
          Sélectionner une promotion...
        </>
      )}
      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
    </Button>
  );

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Promotion</h3>
      </div>
      <p className="mb-4 text-sm text-gray-700">Appliquez une promotion pour réduire le montant total de l'inscription</p>

      <div className="flex gap-2">
        <SelectSearch
          items={promotions}
          selectedItem={selectedPromotion}
          isLoading={loadingPromotions}
          onItemSelect={handlePromotionSelect}
          onCreateNew={handleCreateNew}
          filterFunction={filterPromotions}
          renderItem={renderPromotionItem}
          renderTrigger={renderTrigger}
          placeholder="Rechercher une promotion..."
          createNewText="Créer une nouvelle promotion"
          emptyMessage="Aucune promotion trouvée"
          loadingMessage="Chargement..."
          width="w-[280px]"
          popoverAlign="start"
          showCreateButton={true}
          closeOnSelect={true}
          isMultiSelect={false}
        />

        {/* Bouton pour supprimer la promotion sélectionnée */}
        {selectedPromotionId && (
          <Button variant="outline" size="icon" onClick={() => onPromotionChange(null)}>
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Affichage de la promotion sélectionnée */}
      {selectedPromotion && (
        <div className="mt-3 rounded-md border border-green-200 bg-green-50 p-3">
          <div className="flex items-center gap-2 text-green-800">
            <span className="font-medium">{selectedPromotion.name}</span>
          </div>
          <p className="mt-1 text-sm text-green-700">Réduction de {formatPromotionValue(selectedPromotion)} appliquée</p>
        </div>
      )}

      {/* Formulaire de création/modification de promotion */}
      <PromotionForm
        open={showPromotionForm}
        onOpenChange={setShowPromotionForm}
        onPromotionCreated={(promotion) => handlePromotionCreated(promotion as PromotionBase)}
      />
    </div>
  );
}
